"use client";
import * as React from "react";
import { motion } from "framer-motion";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import aiImg from "../../assets/About_Us_Images/artificial-intelligence.webp";
import entImg from "../../assets/About_Us_Images/Enterprise software.webp";
import erpImg from "../../assets/About_Us_Images/erp.webp";
import crmImg from "../../assets/About_Us_Images/crm.webp";
import hrmsImg from "../../assets/About_Us_Images/HRMS.webp";
import iaImg from "../../assets/About_Us_Images/Industrial Automation.webp";
import iiotImg from "../../assets/About_Us_Images/Industrial IOT.webp";
import cloudImg from "../../assets/About_Us_Images/cloud computing.webp";
import devopsImg from "../../assets/About_Us_Images/devops.webp";
import bpaImg from "../../assets/About_Us_Images/Bussiness process Automation.webp";
import csImg from "../../assets/About_Us_Images/custom software.webp";
import saasImg from "../../assets/About_Us_Images/saas.webp";
import dtImg from "../../assets/About_Us_Images/digital-transformation.webp";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type LogoEntry = {
  icon: React.ReactNode;
  name?: string;
  id?: string;
};

export type LogoCloudSwapProps = {
  logos?: LogoEntry[];
  title?: string;
  subtitle?: string;
  interval?: number;
  stagger?: number;
  className?: string;
};

const WIPE_DURATION = 0.92;
const WIPE_TIMES = [0, 0.4, 1];

const RAW_LOGOS = [
  { src: aiImg, name: "Artificial Intelligence" },
  { src: entImg, name: "Enterprise Software" },
  { src: erpImg, name: "ERP" },
  { src: crmImg, name: "CRM" },
  { src: hrmsImg, name: "HRMS" },
  { src: iaImg, name: "Industrial Automation" },
  { src: iiotImg, name: "Industrial IoT" },
  { src: cloudImg, name: "Cloud Computing" },
  { src: devopsImg, name: "DevOps" },
  { src: bpaImg, name: "Business Process Automation" },
  { src: csImg, name: "Custom Software" },
  { src: saasImg, name: "SaaS" },
  { src: dtImg, name: "Digital Transformation" },
];

const DEFAULT_LOGOS: LogoEntry[] = RAW_LOGOS.map((l) => ({
  icon: (
    <img src={l.src} alt={l.name} className="h-full w-full object-contain" />
  ),
  name: l.name,
  id: l.name,
}));

function LogoItem({
  logo,
  index,
  isWaving,
  stagger,
  totalCount,
  onDone,
}: {
  logo: LogoEntry;
  index: number;
  isWaving: boolean;
  stagger: number;
  totalCount: number;
  onDone: () => void;
}) {
  return (
    <motion.div
      aria-label={logo.name ?? "Logo"}
      animate={
        isWaving
          ? {
              clipPath: [
                "inset(0 0% 0 0)",
                "inset(0 100% 0 0)",
                "inset(0 0% 0 0)",
              ],
              filter: ["blur(0px)", "blur(8px)", "blur(0px)"],
              opacity: [1, 0.2, 1],
            }
          : {
              clipPath: "inset(0 0% 0 0)",
              filter: "blur(0px)",
              opacity: 1,
            }
      }
      transition={
        isWaving
          ? {
              clipPath: {
                duration: WIPE_DURATION,
                times: WIPE_TIMES,
                ease: ["easeIn", [0.16, 1, 0.3, 1]],
                delay: index * stagger,
              },
              filter: {
                duration: WIPE_DURATION * 0.9,
                times: WIPE_TIMES,
                ease: "easeInOut" as const,
                delay: index * stagger,
              },
              opacity: {
                duration: WIPE_DURATION * 0.85,
                times: WIPE_TIMES,
                ease: "easeInOut" as const,
                delay: index * stagger,
              },
            }
          : {
              duration: 0.3,
              ease: "easeOut",
            }
      }
      onAnimationComplete={() => {
        if (isWaving && index === totalCount - 1) onDone();
      }}
      whileHover={{
        scale: 1.07,
        opacity: 1,
        filter: "blur(0px)",
        transition: { type: "spring", stiffness: 340, damping: 24 },
      }}
      className="flex w-24 shrink-0 cursor-default flex-col items-center gap-2 sm:w-28"
    >
      <span className="flex h-10 w-10 items-center justify-center sm:h-12 sm:w-12">
        {logo.icon}
      </span>
      {logo.name && (
        <span className="select-none text-center text-xs font-medium tracking-wide text-gray-400 sm:text-sm">
          {logo.name}
        </span>
      )}
    </motion.div>
  );
}

export default function LogoCloudSwap({
  logos = DEFAULT_LOGOS,
  title = "Our Technology Expertise",
  subtitle = "Vivify Soft Tech works across technology areas including:",
  interval = 3200,
  stagger = 0.11,
  className,
}: LogoCloudSwapProps) {
  const [waving, setWaving] = React.useState(false);

  React.useEffect(() => {
    const id = setInterval(() => setWaving(true), interval);
    return () => clearInterval(id);
  }, [interval]);

  return (
    <section
      className={cn("w-full bg-transparent px-4", className)}
      style={{ padding: '6rem 0' }}
    >
      <div className="mx-auto max-w-2xl text-center" style={{ textAlign: 'center', margin: '0 auto', width: '100%' }}>
        <h2 className="text-2xl font-bold tracking-tight text-white sm:text-4xl" style={{ fontFamily: 'var(--font-display)', marginBottom: '0.5rem', textAlign: 'center' }}>
          {title}
        </h2>
        {subtitle && (
          <p className="mt-3 text-lg text-gray-400" style={{ textAlign: 'center' }}>{subtitle}</p>
        )}
      </div>

      <div className="mx-auto mt-10 max-w-6xl sm:mt-12">
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-10 lg:gap-12">
          {logos.map((logo, i) => (
            <LogoItem
              key={logo.id ?? i}
              logo={logo}
              index={i}
              isWaving={waving}
              stagger={stagger}
              totalCount={logos.length}
              onDone={() => setWaving(false)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
